import React, { useState, useEffect } from 'react';
import { machinesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import { Plus, Edit, Trash2, Wrench, Scissors, Layers, CircleDot } from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'cutting', label: 'Cutting', icon: Scissors, color: 'bg-blue-100 text-blue-800' },
  { value: 'pressing', label: 'Pressing', icon: Layers, color: 'bg-orange-100 text-orange-800' },
  { value: 'boring', label: 'Boring', icon: CircleDot, color: 'bg-purple-100 text-purple-800' }
];

const MachineManagement = () => {
  const { toast } = useToast();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [form, setForm] = useState({
    machine_number: '',
    name: '',
    category: 'cutting',
    is_active: true
  });

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const data = await machinesAPI.getAll();
      setMachines(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load machines', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ machine_number: '', name: '', category: 'cutting', is_active: true });
    setShowDialog(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      machine_number: m.machine_number || '',
      name: m.name || '',
      category: m.category || 'cutting',
      is_active: m.is_active !== false
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.machine_number.trim() || !form.name.trim()) {
      toast({ title: 'Validation', description: 'Machine number and name are required', variant: 'destructive' });
      return;
    }
    try {
      if (editing) {
        await machinesAPI.update(editing.id, form);
        toast({ title: 'Updated', description: `Machine ${form.machine_number} updated` });
      } else {
        await machinesAPI.create(form);
        toast({ title: 'Created', description: `Machine ${form.machine_number} added` });
      }
      setShowDialog(false);
      fetchMachines();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to save machine';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await machinesAPI.delete(id);
      toast({ title: 'Deleted', description: 'Machine removed' });
      setShowDeleteConfirm(null);
      fetchMachines();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to delete machine';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (m) => {
    try {
      await machinesAPI.update(m.id, { is_active: !m.is_active });
      fetchMachines();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const groupedByCategory = CATEGORY_OPTIONS.map(cat => ({
    ...cat,
    machines: machines.filter(m => m.category === cat.value)
  }));
  const uncategorised = machines.filter(m => !m.category || !CATEGORY_OPTIONS.find(c => c.value === m.category));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="machine-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Machines ({machines.length})
          </h2>
          <p className="text-sm text-gray-500">Master list of factory machines. Operators pick from this list when logging work.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-machine-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Machine
        </Button>
      </div>

      {groupedByCategory.map(group => (
        <Card key={group.value} data-testid={`machine-group-${group.value}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <group.icon className="w-4 h-4" />
              <h3 className="font-semibold capitalize">{group.label} Machines</h3>
              <Badge className={group.color}>{group.machines.length}</Badge>
            </div>
            {group.machines.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-3">No {group.label.toLowerCase()} machines added yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="p-2">Code</th>
                      <th className="p-2">Name</th>
                      <th className="p-2">Active</th>
                      <th className="p-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.machines.map(m => (
                      <tr key={m.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-mono font-medium">{m.machine_number}</td>
                        <td className="p-2">{m.name}</td>
                        <td className="p-2">
                          <Switch
                            checked={m.is_active !== false}
                            onCheckedChange={() => handleToggleActive(m)}
                            data-testid={`machine-active-${m.machine_number}`}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`edit-machine-${m.machine_number}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(m)} className="text-red-600 hover:bg-red-50" data-testid={`delete-machine-${m.machine_number}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {uncategorised.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2 text-amber-700">Uncategorised legacy machines ({uncategorised.length})</h3>
            <p className="text-xs text-gray-500 mb-3">Edit these to assign a category so they show up in operator dropdowns.</p>
            <div className="space-y-2">
              {uncategorised.map(m => (
                <div key={m.id} className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <span className="font-mono font-medium">{m.machine_number}</span>
                    <span className="ml-3 text-sm">{m.name}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(m)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(m)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="machine-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Machine' : 'Add Machine'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="machine_number">Machine Code *</Label>
              <Input
                id="machine_number"
                value={form.machine_number}
                onChange={(e) => setForm({ ...form, machine_number: e.target.value })}
                placeholder="e.g., CUT-01"
                data-testid="machine-code-input"
              />
            </div>
            <div>
              <Label htmlFor="name">Display Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Panel Saw 1"
                data-testid="machine-name-input"
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="machine-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="active-toggle">Active (visible to operators)</Label>
              <Switch
                id="active-toggle"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-machine-btn">{editing ? 'Save Changes' : 'Add Machine'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete machine?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{showDeleteConfirm?.machine_number}</strong> ({showDeleteConfirm?.name})?
            Past job card data will be preserved, but operators will no longer see this machine in dropdowns.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(showDeleteConfirm.id)} data-testid="confirm-delete-machine-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MachineManagement;
